/* istanbul ignore file */
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Text,
} from '@chakra-ui/react';
import { SystemTranslationKeys } from '@common/entities';
import styled from '@emotion/styled';
import { RootState } from 'state/store';

import { useSystemTranslation } from 'hooks';
import type { SubCharge } from 'types/shopping-cart';
import {
  calculateChargeTotal,
  getLeaseChargeFrequencyTranslationKey,
  translateChargeDetailText,
} from 'utils/charge-utils';

import CartItem from './CartItem';

interface Props {
  key: string;
  charges: SubCharge[];
  title: string;
  isRecurring?: boolean;
}

export function ChargesAccordionSection({ charges, title, isRecurring }: Props) {
  const { currencyCode } = useSelector((state: RootState) => state.locale);
  const { tSystem } = useSystemTranslation();
  const recurringFrequencies = useMemo(() => {
    if (!isRecurring) return [];
    const frequencies = charges
      .map((charge) => charge.recurringFrequency)
      .filter((f): f is string => f !== undefined && f !== '');
    const uniqueRecurringFrequencies = new Set(frequencies);
    return Array.from(uniqueRecurringFrequencies);
  }, [charges, isRecurring]);

  function getChargesByRecurringFrequency(frequency?: string) {
    if (!frequency) return [];
    return charges.filter((charge) => charge.recurringFrequency === frequency);
  }

  return (
    <AccItems>
      <h2>
        <AccordionButton>
          <Box flex="1" textAlign="left">
            {title}
          </Box>
          <AccIcon />
        </AccordionButton>
      </h2>
      {isRecurring &&
        recurringFrequencies.map((frequency: string, index: number) => {
          const currentSectionCharges = getChargesByRecurringFrequency(frequency);
          const triggerId = currentSectionCharges[0]?.arTriggerId;
          const frequencyTranslationKey = getLeaseChargeFrequencyTranslationKey(triggerId);
          return (
            <AccordionPanel pb={4} key={`${frequency}-${index}`}>
              <Text fontStyle="italic" color="gray.base">
                {frequencyTranslationKey
                  ? tSystem(frequencyTranslationKey)
                  : translateChargeDetailText(tSystem, 'frequency', frequency)}
              </Text>
              {currentSectionCharges.map((charge: SubCharge) => (
                <CartItem
                  key={charge.id}
                  {...charge}
                  currencyCode={currencyCode}
                  taxAmount={Number(charge?.taxAmount?.amount ?? 0)}
                />
              ))}
              {(() => {
                const frequencyCharges = currentSectionCharges;
                const total = calculateChargeTotal(frequencyCharges as any, currencyCode);
                return (
                  <CartItem
                    key={`${frequency}-total-${index}`}
                    label={tSystem(SystemTranslationKeys.cart.total)}
                    total
                    priceNumber={total.minTotal}
                    priceFormatted={total.totalFormatted}
                    currencyCode={total.currencyCode}
                    isRange={total.isRange}
                    minPriceNumber={total.minTotal}
                    maxPriceNumber={total.maxTotal}
                    hasVaries={total.hasVaries}
                  />
                );
              })()}
            </AccordionPanel>
          );
        })}
      {!isRecurring && (
        <AccordionPanel pb={4}>
          {charges &&
            charges.map((charge: SubCharge) => (
              <CartItem
                key={String(charge.id)}
                {...charge}
                currencyCode={currencyCode}
                taxAmount={Number(charge?.taxAmount?.amount ?? 0)}
              />
            ))}
          {(() => {
            const total = calculateChargeTotal((charges || []) as any, currencyCode);
            return (
              <CartItem
                key={charges?.length ?? 0}
                label={tSystem(SystemTranslationKeys.cart.total)}
                total
                priceNumber={total.minTotal}
                priceFormatted={total.totalFormatted}
                currencyCode={total.currencyCode}
                isRange={total.isRange}
                minPriceNumber={total.minTotal}
                maxPriceNumber={total.maxTotal}
                hasVaries={total.hasVaries}
              />
            );
          })()}
        </AccordionPanel>
      )}
    </AccItems>
  );
}

const AccItems = styled(AccordionItem)`
  border-top: 1px solid ${({ theme }: any) => theme.colors.gray.light};
`;
const AccIcon = styled(AccordionIcon)`
  background: ${({ theme }: any) => theme.colors.gray.light};
  border-radius: 4px;
  width: 25px;
  height: 25px;
`;
